import React, { useState } from 'react';
import { CircularProgress, Dialog } from '@material-ui/core';
import * as Sentry from '@sentry/browser';
import { loader } from 'graphql.macro';
import { get } from 'lodash';
import { useMutation } from 'react-apollo';

import { ClientSummaryNode } from '../../generated/webapp_gql';
import { ReactComponent as IconClose } from '../../assets/svgs/icon-close.svg';

export const CREATE_BULK_MESSAGE_MUTATION = loader('./documents/CreateBulkMessageMutation1.gql');

interface Props {
  isOpen: boolean;
  closeDialog: () => void;
  onBackToEdit: () => void;
  onSendBulkMessageSuccess: (startTime: number, endTime: number) => void;
  onSendBulkMessageFailure: (startTime: number, endTime: number) => void;
  bulkMessageText: string;
  membersToBulkMessage: ClientSummaryNode[];
}

export enum BulkMessagePreviewModalTestID {
  BulkMessagePreviewModalCloseButton = 'BulkMessagePreviewModalCloseButton',
  BulkMessagePreviewModalBackButton = 'BulkMessagePreviewModalBackButton',
  BulkMessagePreviewModalSendButton = 'BulkMessagePreviewModalSendButton',
}

export const BulkMessagePreviewModal: React.FC<Props> = ({
  closeDialog,
  isOpen,
  onBackToEdit,
  onSendBulkMessageSuccess,
  bulkMessageText,
  membersToBulkMessage,
  onSendBulkMessageFailure,
}) => {
  const [createBulkMessage] = useMutation(CREATE_BULK_MESSAGE_MUTATION);

  const [sendingMessages, setSendingMessages] = useState(false);

  const sendBulkMessages = async () => {
    const startTime = performance.now();

    setSendingMessages(true);

    try {
      const response = await createBulkMessage({
        variables: {
          clientsUuids: membersToBulkMessage.map((member) => member.memberUuid),
          content: bulkMessageText,
        },
      });

      const data = get(response, 'data.newBulkMessage');
      if (!data) {
        throw new Error(`No bulk message data in the response: ${response}`);
      }

      const endTime = performance.now();
      setSendingMessages(false);
      onSendBulkMessageSuccess(startTime, endTime);

      return { ok: true, data };
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag('Bulk_Message', 'bulk_message');
        scope.setLevel(Sentry.Severity.Error);

        Sentry.captureException('[Error sending bulk message] ' + e);
      });

      const endTime = performance.now();
      setSendingMessages(false);
      onSendBulkMessageFailure(startTime, endTime);

      return { ok: false, error: e };
    }
  };

  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick' && sendingMessages) return;

    closeDialog();
  };

  return (
    <Dialog aria-labelledby="form-dialog-title" fullWidth onClose={handleDialogClose} open={isOpen}>
      <div className="p-4 h-full relative">
        <div
          data-testid={BulkMessagePreviewModalTestID.BulkMessagePreviewModalCloseButton}
          onClick={() => {
            if (sendingMessages) {
              return;
            }
            closeDialog();
          }}
          className="bg-gray-300 hover:bg-gray-200 cursor-pointer rounded-full h-10 w-10 absolute top-4 right-4 flex justify-center items-center"
        >
          <IconClose className="w-3 h-3 text-gray-1000 fill-current stroke-current" />
        </div>
        <div />
        <div className="p-8 flex flex-col">
          <h1 className="font-bold text-black text-2xl mb-4">Sending Bulk Chat Messages</h1>
          <p className="mt-4 text-gray-1000">
            You’re about to send this chat message to {membersToBulkMessage.length}{' '}
            {membersToBulkMessage.length === 1 ? 'client' : 'clients'}
          </p>
          <p className="self-center my-8 bg-blue-200 text-white p-4 w-5/6 rounded-t rounded-l">
            {bulkMessageText}
          </p>
        </div>

        <div className="flex flex-row justify-end">
          {!sendingMessages && (
            <button
              className="mr-4 outline-none h-10 flex font-extrabold justify-center items-center text-blue-200 p-2 px-4 rounded border border-blue-200"
              onClick={onBackToEdit}
              data-testid={BulkMessagePreviewModalTestID.BulkMessagePreviewModalBackButton}
            >
              Back to Edit
            </button>
          )}
          <button
            onClick={sendBulkMessages}
            data-testid={BulkMessagePreviewModalTestID.BulkMessagePreviewModalSendButton}
            className="w-48 outline-none h-10 flex font-extrabold justify-center items-center text-white p-2 px-4 rounded hover:shadow-md bg-blue-200"
          >
            {sendingMessages ? (
              <CircularProgress style={{ color: 'fff' }} color="inherit" size={20} />
            ) : (
              'Send Bulk Messages'
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
